import React, { useEffect, useMemo, useRef, useState } from 'react';
import CheckboxWithIcon from '@/components/CheckboxWithIcon/ChecboxWithIcon';
import Dropdown from '@/components/Dropdown/Dropdown';
import { DropDown, LogoSmall } from '@/resources/assets/images';
import type {
	Business,
	BusinessQuery,
	UserNotes
} from '@/modules/dashboard/dashboardTypes';
import CroppedText from '@/components/CroppedText/CroppedText';
import SortByFilter from '@/modules/communicationCenter/components/CommunicationCenterListItem/SortByFilter';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import {
	dashboardRemoveAllFromSaveList,
	dashboardSetQuery,
	dashboardUpdateBusinessNotesRequest
} from '@/modules/dashboard/dashboardActions';
import { sendAnalytics } from '@/services/analyticsService';
import Loader from '@/components/Loader/Loader';
import HeaderRedesign from '@/modules/main/components/HeaderRedesign/HeaderRedesign';
import { useSelectedBusinessData } from '@/modules/lists/details/src/hooks/useSelectedBusinessData';
import { useSelectBusinessMutation } from '@/modules/lists/details/src/hooks/useSelectBusinessMutation';
import { cn } from '@/lib/classNames';
import { UserRole } from '@/modules/user/userTypes';
import { useHistory } from 'react-router-dom';
import { useIsScreenWidthSmallerThan } from '@/hooks/useIsScreenWidthSmallerThan';
import { useBusinessInfiniteScroll } from '../hooks/useBusinessInfiniteScroll';
import InfiniteScroll from '../components/InfiniteScroll';
import Filter from '../components/BusinessesFilter/Filter';
import { useSequencesContext } from '../SequencesContext';
import { BusinessListType, useBusinessList } from '../hooks/useBusinessLists';
import { SearchIconNew } from '../components/sequencesPage/SequencesContainerHeader';
import NewSequenceFooter from '../components/NewSequenceFooter';
import { BusinessPreview as SequencesBusinessPreview } from '../components/BusinessPreview';

export default function SequenceAddBusinessPage() {
	const { setSequencePage } = useSequencesContext();
	const user = useSelector((state: RootState) =>
		state.user.getIn(['authorize', 'user']).toJS()
	);
	const history = useHistory();

	const screenSize = useIsScreenWidthSmallerThan(1024);
	const { mutate: mutateSelectBusiness } = useSelectBusinessMutation();

	function goBackHomePage() {
		setSequencePage('MY-SEQUENCES');
		// Remove listId from the URL
		const searchParams = new URLSearchParams(window.location.search);
		searchParams.delete('listId');
		history.replace({ search: searchParams.toString() });
		mutateSelectBusiness(null);
	}

	const [selectedListPrevious] = useSelector((state: RootState) => [
		state.dashboard.getIn(['list', 'selected'])
	]);

	const { setSelectedLists, selectedLists, businessLists } =
		useSequencesContext();

	const { selectedBusiness } = useSelectedBusinessData();

	const [listSearch, setListSearch] = useState<string>('');

	const filteredLists = useMemo(
		() =>
			businessLists.filter(list =>
				list.name.toLowerCase().includes(listSearch.toLowerCase())
			),
		[businessLists, listSearch]
	);

	useEffect(() => {
		if (selectedListPrevious) {
			setSelectedLists(prevSelectedLists => [
				...prevSelectedLists,
				selectedListPrevious
			]);
		}
	}, [selectedListPrevious, setSelectedLists]);

	const { fetchBusinessesInLists, fetchBusinessesLists } = useBusinessList();
	const [shouldRefetch, setShouldRefetch] = useState(false);

	useEffect(() => {
		if (!shouldRefetch) return;
		fetchBusinessesInLists();
		setShouldRefetch(false);
	}, [shouldRefetch]);

	useEffect(() => {
		// On mount
		fetchBusinessesLists();
	}, []);

	useEffect(() => {
		if (selectedLists.length > 0) {
			fetchBusinessesInLists();
		}
	}, [selectedLists]);

	function renderPanels() {
		if (selectedBusiness?._id && !screenSize.isSmaller) {
			return (
				<SequencesBusinessPreview className="static !m-0 hidden flex-shrink-0 rounded-lg lg:block" />
			);
		}

		if (screenSize.isSmaller && isUserOnboarding) {
			return (
				<div className="flex h-full w-full flex-col gap-3">
					{!isUserOnboarding && <BusinessesFilter />}
					<div className="h-full overflow-hidden rounded-lg border-dm-grey bg-dm-black">
						{selectedLists.length > 0 ? (
							<BusinessTableContainer />
						) : (
							<BusinessSelectTooltip />
						)}
					</div>
				</div>
			);
		}

		return (
			<div className="flex h-full w-full max-w-xl flex-shrink-0 flex-col gap-3 overflow-hidden rounded-lg border border-dm-grey bg-dm-black p-4 lg:col-span-1 lg:px-6 lg:pt-6">
				<span className="flex-shrink-0 text-lg font-semibold text-white">
					Add business to this sequence
				</span>
				<div className="relative w-full flex-shrink-0">
					<input
						type="text"
						placeholder="Search a list"
						className="h-8 w-full rounded-[4px] border border-dm-grey py-1 pl-2 pr-8 text-sm text-dm-grey"
						value={listSearch}
						onChange={e => setListSearch(e.target.value)}
					/>
					<div className="pointer-events-none absolute bottom-0 right-4 top-0 flex w-3 items-center text-orange">
						<SearchIconNew />
					</div>
				</div>
				<div className="flex h-full flex-col overflow-y-auto overflow-x-hidden rounded-lg border border-dm-grey">
					{filteredLists.map(list => (
						<BusinessesListItem key={list.id} list={list} />
					))}
				</div>
			</div>
		);
	}

	const isUserOnboarding = user?.onboarding?.status === 'pending';

	return (
		<div className="flex h-full flex-col xl:pr-2">
			<HeaderRedesign
				backButton={{
					goBack: goBackHomePage,
					label: 'Back to My Sequences'
				}}
				title="New Sequence creation"
			/>
			<div className="mb-2 mt-3 flex h-full gap-x-3 overflow-hidden">
				{renderPanels()}
				<div className="hidden h-full w-full flex-col gap-3 lg:flex">
					{!isUserOnboarding && <BusinessesFilter />}
					<div className="h-full overflow-hidden rounded-lg border-dm-grey bg-dm-black">
						{selectedLists.length > 0 ? (
							<BusinessTableContainer />
						) : (
							<BusinessSelectTooltip />
						)}
					</div>
				</div>
			</div>
			<NewSequenceFooter />
		</div>
	);
}

function BusinessesFilter() {
	const [query] = useSelector(
		(state: RootState) => [state.dashboard.get('query').toJS()],
		shallowEqual
	);

	const [areFiltersOpen, setAreFiltersOpen] = useState(false);
	const [shouldRefetch, setShouldRefetch] = useState(false);
	const [businessSearch, setBusinessSearch] = useState('');
	const [sortByFocused, setSortByFocused] = useState(false);
	const usedBusinessSearch = useRef(false);

	const { setHasMoreBusinessesToFetch, selectedLists } =
		useSequencesContext();
	const { fetchBusinessesInLists, fetchBusinessesLists } = useBusinessList();

	useEffect(() => {
		if (!shouldRefetch) return;
		fetchBusinessesInLists();
		setShouldRefetch(false);
	}, [shouldRefetch]);

	useEffect(() => {
		// On mount
		fetchBusinessesLists();
	}, []);

	useEffect(() => {
		if (selectedLists.length > 0) {
			fetchBusinessesInLists();
		}
	}, [selectedLists]);

	const dispatch = useDispatch();

	const removeAllFromSaveList = () =>
		dispatch(dashboardRemoveAllFromSaveList());

	const setQuery = (q: BusinessQuery) => {
		removeAllFromSaveList();
		dispatch(dashboardSetQuery(q));
		setHasMoreBusinessesToFetch(true);
		setShouldRefetch(true);
	};

	const setNewQuery = (key: string, newValue: any) => {
		const newQuery = { ...query };
		if (key === 'sortBy') {
			switch (newValue) {
				case 'Sort A-Z by name':
					newQuery['sort'] = { name: 1 };
					break;
				case 'Sort Z-A by name':
					newQuery['sort'] = { name: -1 };
					break;
				case 'Last contacted date':
					newQuery['sort'] = { lastContact: -1 };
					break;
				case 'Last reply date':
					newQuery['sort'] = { lastReply: -1 };
					break;
				case 'Last open date':
					newQuery['sort'] = { lastOpen: -1 };
					break;
			}
		}

		newQuery[key] = newValue;
		newQuery['skip'] = 0;
		setQuery(newQuery);
	};

	const handleSortBySelect = (option: string) => {
		setNewQuery('sortBy', option);
	};

	function handleBusinessSearch(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		if (businessSearch === '') return;
		usedBusinessSearch.current = true;
		setNewQuery('includes', JSON.stringify([businessSearch]));
		setShouldRefetch(true);
	}

	function handleBlur() {
		if (businessSearch === '' && usedBusinessSearch.current) {
			setNewQuery('includes', undefined);
			setShouldRefetch(true);
			usedBusinessSearch.current = false;
		}
	}

	const sortByOptions = ['Sort A-Z by name', 'Sort Z-A by name'];

	return (
		<div
			style={{
				height: areFiltersOpen ? '70px' : '32px'
			}}
			className="flex w-full flex-shrink-0 items-center gap-4 rounded-lg bg-dm-black3 px-4 py-1"
		>
			{areFiltersOpen && (
				<>
					<div
						style={{
							height: '38px'
						}}
						className="relative w-4/6"
					>
						<form onSubmit={handleBusinessSearch}>
							<input
								type="text"
								placeholder="Search businesses"
								className="mr-4 h-full w-full rounded-md border border-dark-grey bg-dm-black px-5 py-2"
								value={businessSearch}
								onBlur={handleBlur}
								onChange={e => {
									setBusinessSearch(e.target.value);
								}}
							/>
							<button
								type="button"
								className="absolute bottom-0 right-2 top-0 flex w-10 items-center justify-center"
							>
								<SearchIconNew />
							</button>
						</form>
					</div>
					<div
						style={{
							width: '150px',
							fontSize: '16px',
							fontWeight: '600'
						}}
						className="flex-shrink-0"
					>
						<Filter />
					</div>
					<SortByFilter
						handleSortBySelect={handleSortBySelect}
						searchFocused={sortByFocused}
						setSearchFocused={setSortByFocused}
						sortByOptions={sortByOptions}
					/>
				</>
			)}
			<button
				type="button"
				onClick={() => setAreFiltersOpen(!areFiltersOpen)}
				className="ml-auto flex w-28 flex-shrink-0 items-center border-l border-dm-grey pl-2 text-sm font-semibold text-dm-mid-grey"
			>
				{areFiltersOpen ? 'Hide filters' : 'Show filters'}
				<DropDown
					width={10}
					height={16}
					className={`ml-2 inline-block transform text-light-grey transition-transform ${
						areFiltersOpen ? 'rotate-180' : ''
					}`}
				/>
			</button>
		</div>
	);
}

function BusinessSelectTooltip() {
	return (
		<div className="h-full rounded-lg p-10">
			<div className="rounded-xl bg-dm-black3 p-12">
				<div className="flex flex-col gap-10">
					<div className="flex items-center gap-5">
						<LogoSmall />
						<h6 className="text-lg font-semibold text-gray-200">
							Would you like to select a list?
						</h6>
					</div>
					<span className="text-light-grey">
						You can start by selecting one list. All businesses in
						it will be automatically selected in your sequence.{' '}
						<br />
						<br />
						You can uncheck any businesses you don't want to
						include. <br />
						<br />
						If needed, select multiple lists to encompass a wider
						range of businesses in your sequence. <br />
						<br />
						After finalizing your selections, click 'Review' to
						enter the Send All mode, where you can review and
						approve the initial emails. <br />
						<br />
						You can also “Skip” adding businesses, and adjust your
						selection from the My Sequences homepage later.
					</span>
				</div>
			</div>
		</div>
	);
}

export function BusinessTableContainer() {
	const { businessesFirstLoading } = useBusinessList();
	const { isBusinessesLoading } = useSequencesContext();

	return (
		<div className="grid h-full overflow-hidden rounded-lg border border-dark-grey text-sm lg:text-base">
			<div className="relative overflow-x-auto">
				<table className="flex h-full w-full min-w-[38rem] flex-col text-gray-200">
					<BusinessesTableHeader />
					<tbody className="flex h-full flex-col overflow-y-auto pr-2">
						{isBusinessesLoading && businessesFirstLoading ? (
							<tr className="flex h-full items-center justify-center">
								<td>
									<Loader />
								</td>
							</tr>
						) : (
							<BusinessesInfiniteScrollList
								businessesFirstLoading={businessesFirstLoading}
								isBusinessesLoading={isBusinessesLoading}
							/>
						)}
					</tbody>
				</table>
			</div>
		</div>
	);
}

function BusinessesInfiniteScrollList({
	isBusinessesLoading,
	businessesFirstLoading
}: {
	isBusinessesLoading: boolean;
	businessesFirstLoading: boolean;
}) {
	const { businessesInLists } = useSequencesContext();
	const { onIntersect } = useBusinessInfiniteScroll();

	return (
		<>
			{businessesInLists.map(business => {
				return (
					<BusinessesTableItem
						key={business._id}
						business={business}
					/>
				);
			})}
			{/* Div to intersect and load more */}
			<tr>
				<td>
					<InfiniteScroll onIntersect={onIntersect} />
				</td>
			</tr>
			{isBusinessesLoading && !businessesFirstLoading && (
				<tr className="flex h-8 items-center justify-center">
					<td>
						<Loader className="w-5" />
					</td>
				</tr>
			)}
		</>
	);
}

function BusinessesTableItem({ business }: { business: Business }) {
	const {
		selectedBusinesses,
		setSelectedBusinesses,
		setBusinessesToRemove,
		businessesAlreadyInSequence,
		setBusinessesToInclude,
		allBusinessesSelect
	} = useSequencesContext();

	const { mutate: mutateSelectBusiness } = useSelectBusinessMutation();

	const isSelected = selectedBusinesses.some(
		selectedBusiness => selectedBusiness._id === business._id
	);

	const isInSequence = businessesAlreadyInSequence.has(business._id);

	function handleSelectBusiness() {
		if (isSelected) {
			setSelectedBusinesses(
				selectedBusinesses.filter(
					selectedBusiness => selectedBusiness._id !== business._id
				)
			);
			if (allBusinessesSelect === 'SELECTED_ALL') {
				// @ts-ignore
				setBusinessesToRemove(prev => [...prev, business._id]);
			}

			if (allBusinessesSelect === 'UNSELECTED_ALL') {
				// @ts-ignore
				setBusinessesToInclude(prev =>
					prev.filter(
						// @ts-ignore
						businessToInclude =>
							businessToInclude.businessId !== business._id
					)
				);
			}
		} else {
			setSelectedBusinesses([...selectedBusinesses, business]);
			if (allBusinessesSelect === 'SELECTED_ALL') {
				// @ts-ignore
				setBusinessesToRemove(prev =>
					// @ts-ignore
					prev.filter(id => id !== business._id)
				);
			}
			if (allBusinessesSelect === 'UNSELECTED_ALL') {
				// @ts-ignore
				setBusinessesToInclude(prev => [
					...prev,
					{ businessId: business._id, listId: business.listId }
				]);
			}
		}
	}

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const isUserOnboardingAndTrial =
		user?.onboarding?.status === 'pending' &&
		user?.role === UserRole.PreTrial;

	const { selectedBusiness } = useSelectedBusinessData();
	const dispatch = useDispatch();

	const setBusinessNotes = (businessId: string, notes: UserNotes) =>
		dispatch(dashboardUpdateBusinessNotesRequest({ businessId, notes }));

	const setLead = (leadStatus: string) => {
		let lead;
		if (leadStatus === 'Lead') {
			lead = true;
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `Marked a business 'Lead'`,
				properties: {
					source: 'Sequences',
					business: business?.name,
					businessId: business._id
				}
			});
		} else if (leadStatus === 'No Lead') {
			lead = false;
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `Marked a business 'No Lead'`,
				properties: {
					source: 'Sequences',
					business: business?.name,
					businessId: business._id
				}
			});
		} else if (leadStatus === 'Lead Status') {
			lead = undefined;
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `Remove lead status from a business`,
				properties: {
					source: 'Sequences',
					business: business?.name,
					businessId: business._id
				}
			});
		}
		const notes = {
			lead,
			isContacted: business.isContacted,
			note: business.note,
			contactType: business.contactType,
			userAddedData: business.userAddedData
		};

		setBusinessNotes(business._id, notes);
	};

	const dropdownTitle = () => {
		if (business?.lead === true) {
			return 'Lead';
		}

		if (JSON.stringify(business?.lead) === 'false') {
			return 'No Lead';
		}

		return 'Lead Status';
	};

	return (
		<tr
			data-isSelected={selectedBusiness?._id === business?._id}
			onClick={() => {
				mutateSelectBusiness(business);
			}}
			className={cn(
				'flex items-center border-b border-dark-grey duration-300 hover:scale-[1.005] hover:cursor-pointer hover:bg-dm-black3 hover:shadow-[0px_3px_5px_#000] lg:justify-between',
				isInSequence && 'opacity-50',
				selectedBusiness?._id === business?._id &&
					'bg-dm-dark-gold hover:bg-dm-dark-gold'
			)}
		>
			<td className="mr-6 p-6">
				<div className="h-6 w-6">
					<CheckboxWithIcon
						checked={isSelected}
						onToggle={handleSelectBusiness}
						disabled={isInSequence}
					/>
				</div>
			</td>
			<td className="w-[max(15%,9.375rem)] flex-shrink-0">
				<CroppedText text={business.name} maxLength={19} />
			</td>
			<td className="w-[max(10%,5rem)] flex-shrink-0 text-center">
				{Math.floor(business.score || 0)}
			</td>
			<td className="hidden w-[max(20%,10rem)] flex-shrink-0 text-left lg:table-cell">
				<CroppedText
					text={String(business.categories.display)}
					maxLength={19}
				/>
			</td>
			{/* Hide the Locations column below 1420px */}
			<td className="hidden w-[max(20%,12.5rem)] text-center xxl:table-cell">
				{business.locations}
			</td>
			{/* Hide the Leads column below 1420px */}
			{!isUserOnboardingAndTrial && (
				<td className="w-[max(15%,6rem)] text-center">
					{business.isContacted === true ? 'Yes' : ''}
				</td>
			)}
			{!isUserOnboardingAndTrial && (
				<td className="hidden w-[max(15%,9rem)] xxl:table-cell">
					<Dropdown
						title={dropdownTitle()}
						headerColor="medium"
						dropdownColor="medium"
						setLead={setLead}
						list={[
							{ id: 1, title: 'Lead', borderColor: 'green' },
							{ id: 2, title: 'No Lead', borderColor: 'red' },
							{
								id: 3,
								title: 'Lead Status',
								borderColor: 'none'
							}
						]}
					/>
				</td>
			)}
		</tr>
	);
}

function BusinessesTableHeader() {
	const {
		selectedBusinesses,
		setSelectedBusinesses,
		businessesInLists,
		setBusinessesToRemove,
		businessesAlreadyInSequence,
		setAllBusinessesSelect,
		setBusinessesToInclude
	} = useSequencesContext();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const isUserOnboardingAndTrial =
		user?.onboarding?.status === 'pending' &&
		user?.role === UserRole.PreTrial;

	const totalNumberOfBusinesses = businessesInLists.length;

	const allSelected = selectedBusinesses.length === totalNumberOfBusinesses;

	function setAllBusinessesSelected() {
		if (allSelected) {
			// Unselecting
			// @ts-ignore
			setSelectedBusinesses(prev => {
				// @ts-ignore
				return prev.filter(b => businessesAlreadyInSequence.has(b._id));
			});
			setBusinessesToRemove([]);
			setAllBusinessesSelect('UNSELECTED_ALL');
		} else {
			// Selecting
			setAllBusinessesSelect('SELECTED_ALL');
			setSelectedBusinesses(businessesInLists);
			setBusinessesToRemove([]);
			setBusinessesToInclude([]);
		}
	}

	return (
		<thead className="border-b border-dark-grey pr-4">
			<tr className="flex items-center lg:justify-between">
				<th
					className="mr-6 border-r border-dark-grey px-6 py-4"
					scope="col"
				>
					<div className="h-6 w-6">
						<CheckboxWithIcon
							checked={allSelected}
							onToggle={setAllBusinessesSelected}
							color="yellow"
						/>
					</div>
				</th>
				<th
					className="w-[max(15%,9.375rem)] flex-shrink-0 text-left font-medium"
					scope="col"
				>
					Name
				</th>
				<th
					className="w-[max(10%,5rem)] flex-shrink-0 text-center font-medium"
					scope="col"
				>
					RE2 Score
				</th>
				<th
					className="hidden w-[max(20%,10rem)] flex-shrink-0 text-left font-medium lg:table-cell"
					scope="col"
				>
					Categories
				</th>
				<th
					className="hidden w-[max(20%,12.5rem)] text-center font-medium xxl:table-cell"
					scope="col"
				>
					Location
				</th>
				{!isUserOnboardingAndTrial && (
					<th
						className="w-[max(15%,6rem)] text-center font-medium"
						scope="col"
					>
						Contacted
					</th>
				)}
				{!isUserOnboardingAndTrial && (
					<th
						className="hidden w-[max(15%,9rem)] flex-shrink-0 text-left font-medium xxl:table-cell"
						scope="col"
					>
						Leads
					</th>
				)}
			</tr>
		</thead>
	);
}

function BusinessesListItem({ list }: { list: BusinessListType }) {
	const {
		selectedSequence,
		selectedLists,
		setSelectedLists,
		setSelectedBusinesses,
		setBusinessesScrollPage,
		setHasMoreBusinessesToFetch,
		setBusinessesInLists,
		setBusinessesToRemove,
		setAllBusinessesSelect,
		setBusinessesToInclude
	} = useSequencesContext();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const isSelected = selectedLists.some(
		selectedList => selectedList.id === list.id
	);

	const handleSelectList = () => {
		setBusinessesScrollPage(1);
		setHasMoreBusinessesToFetch(true);

		if (isSelected) {
			const filteredLists = selectedLists.filter(
				selectedList => selectedList.id !== list.id
			);
			setSelectedLists(filteredLists);

			// If no list is selected then we reset the state
			if (filteredLists.length === 0) {
				setAllBusinessesSelect('SELECTED_ALL');
				setBusinessesToInclude([]);
				setBusinessesToRemove([]);
			}

			// @ts-ignore
			setBusinessesInLists(prev =>
				// @ts-ignore
				prev.filter(business => business.listId !== list.id)
			);

			// @ts-ignore
			setSelectedBusinesses(prev =>
				prev.filter(
					// @ts-ignore
					selectedBusiness => selectedBusiness.listId !== list.id
				)
			);

			// @ts-ignore
			setBusinessesToRemove(prev =>
				prev.filter(
					// @ts-ignore
					b => !list.businessIds.includes(b)
				)
			);

			setBusinessesToInclude(
				// @ts-ignore
				prev => prev.filter(b => b.listId !== list.id)
			);
		} else {
			setAllBusinessesSelect('SELECTED_ALL');
			setSelectedLists([...selectedLists, list]);

			sendAnalytics({
				type: 'track',
				userId: user.email,
				sequenceId: selectedSequence?.id,
				listId: list.id,
				businessCount: list.businessIds.length,
				eventName: 'user_selected_list_to_enroll_sequences'
			});
		}
	};

	return (
		<div className="flex h-[72px] flex-shrink-0 items-center gap-3 border-b border-dm-grey pl-6 pr-8 lg:gap-6">
			<div className="h-6 w-6">
				<CheckboxWithIcon
					checked={isSelected}
					onToggle={handleSelectList}
				/>
			</div>
			<div className="flex w-[272px] flex-col gap-1 pl-4 pr-8">
				<span className="line-clamp-1 font-semibold text-dm-white">
					{list.name}
				</span>
				<span className="text-sm text-dm-mid-grey">
					{list.businessIds.length} businesses
				</span>
			</div>
		</div>
	);
}
